// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { Badge } from '@loa/ui-components';
import type { QuoteTermOption, QuoteTermAllocationBand } from '../../api/quoteTermApi';
import { humanizeMetadataKey } from './metadataLabel';
import './QuoteOptionCard.css';

type Props = Readonly<{ option: QuoteTermOption }>;

const pct = (v?: number | null, decimals = 2): string | null => {
  if (v == null) return null;
  return Number.isInteger(v) ? `${v}%` : `${v.toFixed(decimals)}%`;
};

const money = (v?: number | null): string | null =>
  v == null ? null : `€${v.toLocaleString('en-IE')}`;

const schedule = (values?: number[] | null): string => {
  if (!values || values.length === 0) return 'NIL';
  return values.map((v) => pct(v, 1)).join(', ');
};

const bandRange = (b: QuoteTermAllocationBand): string => {
  const lo = money(b.contributionMin);
  const hi = money(b.contributionMax);
  if (lo && hi) return `${lo}–${hi}`;
  if (lo) return `${lo}+`;
  if (hi) return `up to ${hi}`;
  return 'all';
};

/** Allocation for the option: flat rate, or a compact banded summary. */
const allocationValue = (option: QuoteTermOption): string => {
  if (option.allocationBasis === 'FLAT') return pct(option.flatAllocationRate, 2) ?? '—';
  const bands = option.bands ?? [];
  if (bands.length === 0) return '—';
  return bands.map((b) => `${pct(b.allocationRate, 2)} (${bandRange(b)})`).join(' · ');
};

const commissionValue = (option: QuoteTermOption): string | null => {
  if (option.initialCommissionFixed != null) return `${pct(option.initialCommissionFixed)} fixed`;
  const min = pct(option.initialCommissionMin);
  const max = pct(option.initialCommissionMax);
  if (min && max) return `${min}–${max}`;
  if (max) return `up to ${max}`;
  return null;
};

const fundBasedValue = (option: QuoteTermOption): string | null => {
  const min = pct(option.fundBasedCommissionMin);
  const max = pct(option.fundBasedCommissionMax);
  const range = min && max ? `${min}–${max}` : (max ?? min ?? null);
  if (!range) return option.accrualRate ? `accrual ${option.accrualRate}` : null;
  return option.accrualRate ? `${range} (accrual ${option.accrualRate})` : range;
};

const fundChargeValue = (option: QuoteTermOption): string | null => {
  const parts = [
    pct(option.standardFundCharge) && `Std ${pct(option.standardFundCharge)}`,
    pct(option.passiveFundCharge) && `Passive ${pct(option.passiveFundCharge)}`,
    pct(option.fixedAllocationFundCharge) && `Fixed-alloc ${pct(option.fixedAllocationFundCharge)}`,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
};

const contributionValue = (option: QuoteTermOption): string | null => {
  const min = money(option.minContribution);
  const max = money(option.maxContribution);
  if (min && max) return `${min} – ${max}`;
  if (min) return `from ${min}`;
  if (max) return `up to ${max}`;
  return null;
};

const policyFeeValue = (option: QuoteTermOption): string | null => {
  const amt = money(option.policyFeeAmount);
  if (!amt) return null;
  return option.policyFeeFrequency ? `${amt} / ${option.policyFeeFrequency.toLowerCase()}` : amt;
};

const metadataRows = (option: QuoteTermOption): Array<{ label: string; value: string }> => {
  const meta = option.metadata;
  if (!meta || typeof meta !== 'object') return [];
  return Object.entries(meta)
    .filter(([, v]) => v != null && String(v) !== '')
    .map(([label, v]) => ({ label: humanizeMetadataKey(label), value: String(v) }));
};

/**
 * Read-only view of one provider rate-card option (`QuoteTermOption`) — the
 * "quote parameters with all their options" surface. Reuses the Quote Options
 * card visual (`quote-option-card__name/__rows/__row`) so a rate card reads like
 * the quotes it generates, but drives every field off the rate-card shape
 * (allocation basis + bands, all charges, commission, exit/clawback schedules,
 * contribution range, policy fee, metadata). See quotes-engine-prd.md §7.2.
 */
export function RateCardOptionCard({ option }: Props) {
  const rows: Array<{ label: string; value: string | null }> = [
    { label: 'Allocation', value: allocationValue(option) },
    { label: 'Fund charge', value: fundChargeValue(option) },
    { label: 'Initial commission', value: commissionValue(option) },
    { label: 'Fund-based commission', value: fundBasedValue(option) },
    { label: 'Exit charges', value: schedule(option.exitCharges) },
    { label: 'Clawback', value: schedule(option.commissionClawback) },
    { label: 'Contribution range', value: contributionValue(option) },
    { label: 'Min term', value: option.minTermYears != null ? `${option.minTermYears} yrs` : null },
    { label: 'Policy fee', value: policyFeeValue(option) },
    { label: 'Fund switches', value: option.fundSwitches ?? null },
    { label: 'Customer withdrawals', value: option.customerWithdrawals ?? null },
    { label: 'Max NRA for commission', value: option.maxNraForCommission != null ? String(option.maxNraForCommission) : null },
    ...metadataRows(option),
  ];

  return (
    <div
      className="border border-grey-06 rounded p-4 flex flex-col gap-2"
      data-testid={`rate-card-option-${option.optionCode}`}
    >
      <div className="quote-option-card__header">
        <span className="quote-option-card__name">
          {option.optionCode}
          {option.optionName ? ` · ${option.optionName}` : ''}
        </span>
        <Badge color="neutral" variant="outlined">{option.allocationBasis}</Badge>
      </div>

      <dl className="quote-option-card__rows">
        {rows
          .filter((r) => r.value != null)
          .map((r) => (
            <div className="quote-option-card__row" key={r.label}>
              <dt>{r.label}</dt>
              <dd>{r.value}</dd>
            </div>
          ))}
      </dl>
    </div>
  );
}
